import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ResponsiveTreeMapHtml } from '@nivo/treemap';
import { getUncatchEndpointData } from '../utils/request';
import {
  COLOR_FALLBACK_TECNOLOGIA,
  construirColorPorTecnologia,
  formatearValorTreemap
} from '../utils/estadisticasGdPalette';
import { VDR_CHART, VDR_FONT_BODY, colorTextoSobre } from './vdrChartStyle';

// Tipografía de marca en etiquetas del treemap. El tooltip propio se estiliza en CSS.
const TREEMAP_THEME = {
  text: { fontFamily: VDR_FONT_BODY, fontSize: 12, fill: VDR_CHART.ink },
  labels: { text: { fontFamily: VDR_FONT_BODY, fontSize: 12.5, fontWeight: 600 } }
};

export type FuenteGd = 'pmgd' | 'eg';
export type MetricaTreemap = 'capacidad' | 'cantidad';

type NodoArbol = {
  name: string;
  value?: number;
  children?: NodoArbol[];
};

type TreemapPayload = {
  fuente: FuenteGd;
  region: string | null;
  unidad: 'MW' | 'kW' | 'proyectos';
  metrica: MetricaTreemap;
  nivel: 'tecnologia' | 'tecnologia_comuna';
  arbol: NodoArbol;
};

const TreemapGd: FunctionComponent<{
  fuente: FuenteGd;
  region: string | null;
  metrica?: MetricaTreemap;
  altura?: number;
}> = ({ fuente, region, metrica = 'capacidad', altura = 380 }) => {
  const [payload, setPayload] = useState<TreemapPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const query: Record<string, string> = { fuente, metrica };
        if (region) query.region = region;
        const res = await getUncatchEndpointData({
          endpoint: 'estadisticas-gd/treemap',
          query
        });
        if (cancelado) return;
        setPayload(res as TreemapPayload);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setError('No se pudieron obtener los datos del treemap.');
        setPayload(null);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelado = true;
    };
  }, [fuente, region, metrica]);

  const tecnologias = useMemo(() => {
    if (!payload) return [] as string[];
    return (payload.arbol.children ?? []).map((c) => c.name);
  }, [payload]);

  const colorPorTecnologia = useMemo(
    () => construirColorPorTecnologia(tecnologias),
    [tecnologias]
  );

  const arbolVacio = useMemo(
    () => !payload || (payload.arbol.children ?? []).length === 0,
    [payload]
  );

  if (loading) {
    return (
      <div className='treemap-gd-status' style={{ minHeight: altura }}>
        Cargando...
      </div>
    );
  }

  if (error) {
    return (
      <div className='treemap-gd-status treemap-gd-status--error' style={{ minHeight: altura }}>
        {error}
      </div>
    );
  }

  if (!payload || arbolVacio) {
    return (
      <div className='treemap-gd-status' style={{ minHeight: altura }}>
        No hay datos para los filtros seleccionados.
      </div>
    );
  }

  const unidad = payload.unidad;
  const esPlano = payload.nivel === 'tecnologia';

  return (
    <div className='treemap-gd-container' style={{ height: altura }}>
      {/*
        ResponsiveTreeMapHtml usa <div> con overflow:hidden en cada nodo,
        lo que soluciona el desbordamiento de etiquetas.
      */}
      <ResponsiveTreeMapHtml
        data={payload.arbol as any}
        identity='name'
        value='value'
        valueFormat={(v) => formatearValorTreemap(v, unidad)}
        tile='squarify'
        leavesOnly={esPlano}
        innerPadding={2}
        outerPadding={esPlano ? 0 : 4}
        enableLabel
        labelSkipSize={40}
        label={(node) => `${node.data.name}`}
        labelTextColor={(node: any) => colorTextoSobre(node.color)}
        orientLabel={false}
        enableParentLabel={!esPlano}
        parentLabel={(node) => `${node.data.name}`}
        parentLabelSize={20}
        parentLabelPadding={6}
        parentLabelTextColor={(node: any) => colorTextoSobre(node.color)}
        nodeOpacity={1}
        // Plano: el blanco entre celdas separa. Anidado: las comunas comparten color y el filete las delimita.
        borderWidth={esPlano ? 0 : 1}
        borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
        theme={TREEMAP_THEME}
        colors={(node: any) => {
          const path = node?.pathComponents ?? [];
          const tecnologia: string = path.length >= 2 ? path[1] : (node?.data?.name ?? '');
          if (!tecnologia) return COLOR_FALLBACK_TECNOLOGIA;
          return colorPorTecnologia.get(tecnologia) ?? COLOR_FALLBACK_TECNOLOGIA;
        }}
        animate
        motionConfig='gentle'
        tooltip={({ node }) => {
          const path = node.pathComponents ?? [];
          const tecnologia = path.length >= 2 ? path[1] : node.data.name;
          const comuna = path.length >= 3 ? path[2] : null;
          const valor = formatearValorTreemap(node.value, unidad);
          return (
            <div className='treemap-gd-tooltip'>
              <div
                className='treemap-gd-tooltip-color'
                style={{ background: node.color }}
              />
              <div className='treemap-gd-tooltip-textos'>
                <div className='treemap-gd-tooltip-titulo'>{tecnologia}</div>
                {comuna && <div className='treemap-gd-tooltip-sub'>{comuna}</div>}
                <div className='treemap-gd-tooltip-valor'>{valor}</div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export default TreemapGd;
